<template>
  <v-container class="pa-0">
    <v-app-bar color="transparent" class="elevation-0 pa-0 page-header-toolbar">
      <page-header-title :title="$t('users.title')"></page-header-title>

      <v-spacer></v-spacer>

      <v-checkbox
        v-model="search_params.show_all"
        :label="$t('global.show_all')"
        @change="filter"
        class="mt-8"
        v-if="$vuetify.breakpoint.smAndUp"
      ></v-checkbox>

      <v-form
        ref="form"
        @submit.prevent="filter"
        class="ml-2 mt-8 d-none d-sm-flex"
      >
        <v-container grid-list-md>
          <v-layout row wrap>
            <v-flex xs6>
              <v-text-field
                :label="$t('global.search')"
                v-model="search_params.any"
                @keydown="checkKeyPress"
                autocomplete="off"
              ></v-text-field>
            </v-flex>
            <v-flex xs6>
              <v-select
                v-model="search_params.status"
                :items="$dd_user_status()"
                item-text="text"
                item-value="key"
                :label="$t('users.status')"
                @change="filter"
                clearable
              >
              </v-select>
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>

      <action-button
        type="merge"
        v-if="selectedParticipants.length == 2"
        @click="merge"
      />

      <router-link
        :to="
          BACKEND_URL +
            '/users/account/create' +
            ($hasRole('company') ? '?query_action=add-to-employee-access' : '')
        "
        v-if="$can('user-create')"
      >
        <action-button type="new" :icon_only="breakpointSmAndDown" />
      </router-link>

      <action-button
        type="search"
        :text="$t('users.find_duplicates')"
        @click="findDuplicates"
      />
    </v-app-bar>

    <v-card>
      <v-card-text>
        <v-flex xs6>
          <v-text-field
            :label="$t('global.search')"
            v-model="search_params.any"
            @keydown="checkKeyPress"
            v-if="$vuetify.breakpoint.xsOnly"
            autocomplete="off"
          ></v-text-field>
        </v-flex>
        <v-flex xs6>
          <v-select
            v-if="$vuetify.breakpoint.xsOnly"
            v-model="search_params.status"
            :items="$dd_user_status()"
            item-text="text"
            item-value="key"
            :label="$t('users.status')"
            @change="filter"
            hide-details
            clearable
          >
          </v-select>
        </v-flex>
        <v-flex md12>
          <v-data-table
            :headers="headers"
            :items="users"
            :items-per-page="users.length"
            :options.sync="pagination"
            :footer-props="{
              showFirstLastPage: true,
              'items-per-page-options': this.DATATABLE_ITEM_PER_PAGE_OPTIONS,
              'items-per-page-all-text': $t('global.items_per_page_all_text'),
              'items-per-page-text': $t('global.items_per_page_text'),
              'page-text': $t('global.page_text'),
            }"
            :server-items-length="total"
            :loading="show_loading"
            :loading-text="$t('global.loading_text')"
            :no-data-text="$t('global.no_data_text')"
            :no-results-text="$t('global.no_results_text')"
            item-key="id"
            :show-select="$vuetify.breakpoint.xsOnly ? false : true"
            v-model="selectedParticipants"
            :must-sort="true"
          >
            <template v-slot:item.unique_id="{ item }">
              {{ item.person.unique_id }}
            </template>

            <template v-slot:item.first_name="{ item }">
              {{ item.person.first_name }}
            </template>

            <template v-slot:item.last_name="{ item }">
              {{ item.person.last_name }}
            </template>

            <template v-slot:item.date_of_birth="{ item }">
              {{
                item.person.date_of_birth
                  ? $dateFormatGetter(item.person.date_of_birth)
                  : null
              }}
            </template>

            <template v-slot:item.mobile="{ item }">
              {{ item.person.mobile }}
            </template>

            <template v-slot:item.status="{ item }">
              {{ $dd_user_status(item.status) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <router-link
                :to="{
                  path: BACKEND_URL + '/users/account/edit/' + item.id,
                  props: { id: item.id },
                }"
                v-if="$can('user-access')"
              >
                <action-button
                  small
                  type="edit"
                  :icon_only="true"
                  :text="$t('users.view_details')"
                />
              </router-link>

              <action-button
                small
                type="delete"
                @click="del(item.id)"
                :icon_only="true"
                v-if="$hasRole('system-developer')"
                :disabled="
                  $can('user-delete') &&
                    (Boolean(loggedinuser.id == item.id)
                      ? true
                      : Boolean(
                          admin_count < 2 &&
                            item.roles.find(i => i.slug == 'superuser'),
                        )
                      ? true
                      : false)
                "
              />
            </template>
          </v-data-table>
        </v-flex>
      </v-card-text>
    </v-card>

    <merge-student
      :key="reRenderKey"
      :show="showMergeModal"
      :id1="selectedP1"
      :id2="selectedP2"
      :mode="selectedMergeMode"
      @callback_update_showMergePopup="callback_update_showMergePopup"
      @callback_cancel_findduplicates="callback_cancel_findduplicates"
    />
  </v-container>
</template>

<script>
import { ActionButton } from "@brakomp/ui"

export default {
  name: "UserList",

  components: {
    ActionButton,
    "merge-student": () =>
      import("@/modules/users/components/partials/MergeStudent"),
  },

  data() {
    return {
      url: "",
      users: [],
      admin_count: 0,
      total: 0,
      show_loading: false,
      show_filter: false,
      refresh: false,
      pagination: {
        page: this.$getLocalStorage("__page_User_List", "page"),
        itemsPerPage: this.$getSetting("rows_per_page"),
        sortBy: ["first_name"],
        sortDesc: [false],
      },
      search_params: {
        any: "",
      },

      selectedParticipants: [],
      selectedP1: 0,
      selectedP2: 0,
      showMergeModal: false,

      reRenderKey: 0,
      show_merge_options: false,
      duplicateData: null,
      selectedMergeMode: null,
      selectedMergeCount: 0,
    }
  },

  watch: {
    refresh: {
      handler() {
        if (this.refresh) {
          localStorage.setItem(
            "__filter_User_List",
            JSON.stringify(this.search_params),
          )
          this.list()
        }
      },
    },
    pagination: {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) {
          if (
            _.has(newVal, "sortBy") &&
            _.has(newVal, "sortDesc") &&
            _.has(oldVal, "sortBy") &&
            _.has(oldVal, "sortDesc") &&
            (!_.isEqual(newVal.sortBy, oldVal.sortBy) ||
              !_.isEqual(newVal.sortDesc, oldVal.sortDesc))
          ) {
            let obj = {
              sortBy: this.pagination.sortBy,
              sortDesc: this.pagination.sortDesc,
            }
            localStorage.setItem("__sort_User_List", JSON.stringify(obj))
          }
        }

        if (_.has(newVal, "page") && !_.isEqual(newVal.page, oldVal.page)) {
          localStorage.setItem("__page_User_List", newVal.page)
        }

        if (
          this.search_params.any ||
          this.search_params.status ||
          this.search_params.show_all
        ) {
          this.refresh = true
        }
      },
    },
  },

  computed: {
    breakpointSmAndDown() {
      return this.$vuetify.breakpoint.smAndDown
    },
    headers: {
      get() {
        return [
          { text: this.$t("users.unique_id"), value: "unique_id" },
          { text: this.$t("users.first_name"), value: "first_name" },
          { text: this.$t("users.last_name"), value: "last_name" },
          { text: this.$t("persons.date_of_birth"), value: "date_of_birth" },
          { text: this.$t("persons.mobile"), value: "mobile" },
          { text: this.$t("users.email"), value: "email" },
          { text: this.$t("users.status"), value: "status" },
          {
            text: this.$t("global.actions"),
            value: "actions",
            width: "20%",
            align: "end",
            sortable: false,
          },
        ]
      },
    },
    loggedinuser: {
      get() {
        return this.$store.getters["auth/user"]
      },
    },
  },

  created() {
    this.url = this.API_URL + "users"

    if (localStorage.getItem("__filter_User_List")) {
      this.search_params = JSON.parse(
        localStorage.getItem("__filter_User_List"),
      )
      if (this.search_params.any) {
        this.refresh = true
      }
    }
  },

  methods: {
    list() {
      this.show_loading = true

      let localFilter = localStorage.getItem("__filter_User_List")
      if (localFilter !== null) {
        localFilter = JSON.parse(localFilter)
        this.search_params = localFilter
      }

      let localSort = localStorage.getItem("__sort_User_List")
      if (localSort !== null) {
        localSort = JSON.parse(localSort)
        this.pagination.sortBy = localSort.sortBy
        this.pagination.sortDesc = localSort.sortDesc
      }

      let query = this.$queryString(this.pagination, this.search_params)

      axios
        .get(this.url + "?resourcetype=user-list&" + query)
        .then(res => {
          this.users = res.data.data
          this.total = res.data.meta.total
          this.refresh = false
          this.show_loading = false

          this.pagination.page = res.data.meta.current_page
          this.pagination.itemsPerPage = Number(res.data.meta.per_page)

          axios.get(this.API_URL + "users/admin-count").then(res => {
            this.admin_count = res.data.count
          })
        })
        .catch(err => console.log(err))
    },

    filter() {
      if (
        this.search_params.any ||
        this.search_params.status ||
        this.search_params.show_all
      ) {
        this.pagination.page = 1
        this.refresh = true
      } else {
        localStorage.setItem(
          "__filter_User_List",
          JSON.stringify(this.search_params),
        )

        this.users = []
        this.total = 0
        this.refresh = false
        this.show_loading = false
        this.pagination = {
          page: 1,
          itemsPerPage: this.$getSetting("rows_per_page"),
          sortBy: ["first_name"],
          sortDesc: [false],
        }
      }
    },

    reset() {
      this.$refs.form.reset()
      this.pagination.page = 1
      this.refresh = true
    },

    del(id) {
      let vm = this

      this.$awn.confirm(
        this.$t("global.delete_message"),
        () => {
          return
        },
        () => {
          axios
            .delete(this.API_URL + "user/" + id)
            .then(() => {
              this.refresh = true
              vm.$awn.success(vm.$t("global.delete_success"))
            })
            .catch(err => console.log(err))
        },
        {
          labels: {
            confirm: "",
            confirmCancel: this.$t("global.yes"),
            confirmOk: this.$t("global.no"),
          },
        },
      )
    },

    findDuplicates() {
      this.selectedMergeMode = "find-duplicates"
      this.showMergeModal = true
    },
    callback_cancel_findduplicates() {
      this.selectedMergeMode = null
      this.showMergeModal = false
      this.selectedParticipants = []
    },

    merge() {
      this.selectedP1 = this.selectedParticipants[0].id
      this.selectedP2 = this.selectedParticipants[1].id
      this.selectedMergeMode = null
      this.showMergeModal = true
    },
    callback_update_showMergePopup(reload = false) {
      this.selectedP1 = 0
      this.selectedP2 = 0

      this.showMergeModal = false
      this.selectedMergeMode = null
      this.selectedParticipants = []
      this.reRenderKey++

      this.refresh = reload
    },

    checkKeyPress(e) {
      if (e.key == "Enter") {
        this.filter()
      }
    },
  },
}
</script>

<style>
thead.v-data-table-header-mobile tr th {
  padding: 0 !important;
}
</style>
